/* © 2017 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Aetonix, January 01, 2017
 * For information or permission request, email info@aetonixsystems.com
 */

import React, { Component, Fragment } from "react";
import Indicator from "ae-base/Base/Indicator";
import SignaturePopup from "ae-base/Base/SignaturePopup";
import validator from "is-my-json-valid";
import memoize from "memoizee";
import PropTypes from "prop-types";
import { Platform, View } from "react-native";
import xtend from "xtend";
import errorUtils from "./errorUtils";
import FormInput from "./Inputs/FormInput";
import IncrementalForm from "./Inputs/IncrementalForm";
import SubmitActions from "./SubmitActions";
import { removeDraftFiles } from "./utils/formUtils";
import { noop } from "lodash";

const makeValidator = memoize(validator);

const VALIDATOR_OPTIONS = {
	verbose: true,
	formats: {
		email: /^[^ ]+@[^ ]+$/,
		macaddress: /([0-9A-Fa-f]{2,8}[:-]){4,5}([0-9A-Fa-f]{2,12})/
	}
};

class Form extends Component {
	constructor(props) {
		super(props);

		this.update = memoize(makeUpdater.bind(this));
		this.submit = handleSubmit.bind(this);
		this.getSubmitData = getSubmitData.bind(this);
		this.inputRefs = [];
		const defaultValues = props.defaultData || {};
		this.saveEncryptStorage = props.saveEncryptStorage;

		if (Platform.OS === "web") {
			removeDraftFiles(props.schema.properties || {}, defaultValues);
		}
		this.defaultValues = defaultValues;

		this.state = {
			hasChanged: false,
			errors: [],
			data: defaultValues,
			collapsed: true,
			submitId: Math.random()
		};

		this.renderSubmitAction = this.renderSubmitAction.bind(this);
		this.signaturePopupRef = React.createRef();

		this.getErrors = this.getErrors.bind(this);
	}

	componentWillUnmount() {
		this.inputRefs = null;
	}

	getChildContext() {
		return {
			bigger: this.props.bigger,
			sources: this.props.sources || this.context.sources || {},
			externalForms: this.props.externalForms || this.context.externalForms || {},
			localizeFrom: this.context.localizeFrom || this.props.localizeFrom,
			localize: this.context.localize || this.props.localize,
			dispatch: this.context.dispatch || this.props.dispatch || noop
		};
	}

	getErrors() {
		const externalErrs = this.props.errors;
		let errors;

		if (externalErrs) {
			errors = errorUtils.formatErrors(externalErrs);
		} else {
			errors = this.state.errors;
		}

		return errors;
	}

	renderSubmitAction() {
		const localize = this.context.localize || this.props.localize;
		const localizeFrom = this.context.localizeFrom || this.props.localizeFrom;
		const {
			submitType,
			cancelText,
			onCancel,
			submitStyle,
			textStyle,
			id_prefix = "",
			autoSubmit,
			schema,
			submitText = localizeFrom(schema.doneLocalization, ".") || localize("common.save"),
			isATouchAway
		} = this.props;

		let formSubmitType = submitType;

		if (schema.submitType) {
			formSubmitType = schema.submitType;
		}

		const submit = this.submit;
		if (autoSubmit) {
			//backwards compatibility
			return null;
		}

		const errors = this.getErrors();

		switch (formSubmitType) {
			case "signature": {
				const signatureSubmit = (file) => {
					let data = this.getSubmitData();
					let submitData = Object.assign({}, data, { SIGNATURE_SUBMIT: file });

					if (this.props.onSubmit) {
						this.props.onSubmit(submitData);
					}
				};

				const preSubmit = () => {
					let data = this.getSubmitData();
					this.validateSubmitData(data, openSignatureModal);
				};

				const openSignatureModal = () => {
					this.signaturePopupRef.current.openModal();
				};
				return (
					<Fragment>
						<SubmitActions
							id_prefix={id_prefix}
							onSubmit={preSubmit}
							submitText={localize("form.continue")}
							cancelText={cancelText}
							onCancel={onCancel}
							submitStyle={submitStyle}
							textStyle={textStyle}
							errors={errors}
						/>
						<SignaturePopup
							ref={this.signaturePopupRef}
							onDone={signatureSubmit}
							submitText={submitText}
							isATouchAway={isATouchAway}
						/>
					</Fragment>
				);
			}

			case "auto":
				return null;
			case "basic":
			default:
				return (
					<SubmitActions
						id_prefix={id_prefix}
						onSubmit={submit}
						submitText={submitText}
						cancelText={cancelText}
						onCancel={onCancel}
						submitStyle={submitStyle}
						textStyle={textStyle}
						errors={errors}
					/>
				);
		}
	}
	render() {
		const schema = this.props.schema;

		if (!schema) {
			return <Indicator />;
		}

		const props = this.props;
		const schemaDefaults = defaults(schema);
		const defaultData = xtend(schemaDefaults, this.defaultValues || {});
		const properties = schema.properties || {};
		const dependencies = schema.dependencies || schema.displayDependencies || {};
		const submitText = props.submitText;
		const cancelText = props.cancelText;
		const onCancel = props.onCancel;
		const onChangePage = props.onChangePage;
		const submitStyle = props.submitStyle;
		const textStyle = props.textStyle;
		const labelStyle = props.labelStyle;
		const contrast = props.contrast;
		const id_prefix = props.id_prefix || "";
		const externalForms = this.props.externalForms || this.context.externalForms || {};
		const errors = this.getErrors();
		const update = this.update;
		const submit = this.submit;
		const currentData = this.state.hasChanged ? this.state.data : defaultData;
		const dataId = props.dataId;
		const isATouchAway = props.isATouchAway;
		const showCustomAddButton = props.showCustomAddButton;
		const platform = props.platform;
		const unsupportedFeatures = props.unsupportedFeatures || [];

		if (schema.inputType === "incremental")
			return (
				<View key={this.state.submitId} style={props.style}>
					<IncrementalForm
						id_prefix={id_prefix}
						schema={schema}
						defaultValue={currentData}
						onChange={update}
						onSubmit={submit}
						onCancel={onCancel}
						onChangePage={onChangePage}
						contrast={contrast}
						errors={errors}
						submitText={submitText}
						cancelText={cancelText}
						labelStyle={labelStyle}
						submitStyle={submitStyle}
						textStyle={textStyle}
						subForm={props.subForm}
						dataId={dataId}
						platform={platform}
						unsupportedFeatures={unsupportedFeatures}
						showCustomAddButton = {showCustomAddButton}
					/>
				</View>
			);

		const inputRefs = this.inputRefs;

		const children = Object.keys(properties)
			.filter(function (propertyName) {
				let satisfied = true;
				const dependency = dependencies[propertyName];
				const dependencyType = typeof dependency;

				if (dependencyType === "string") satisfied = currentData[dependency];
				else if (dependency && dependencyType === "object") {
					if (Array.isArray(dependency)) {
						satisfied = dependency.every(function (subDependency) {
							return currentData[subDependency];
						});
					} else {
						const validate = makeValidator(dependency, VALIDATOR_OPTIONS);
						validate(currentData);
						satisfied = !validate.errors;
					}
				}

				return properties[propertyName].inputType !== "hidden" && satisfied;
			})
			.sort(function (a, b) {
				const orderA = properties[a].order;
				const orderB = properties[b].order;

				return orderA - orderB || 0;
			})
			.map(function (propertyName, index, array) {
				const propertySchema = properties[propertyName];
				const defaultValue = currentData[propertyName];
				const propertyErrors = errors && !Array.isArray(errors) ? errors[propertyName] : null;
				const doUpdate = update(propertyName);

				if (propertyName === "SectionUpdateAudit") return null;

				let inputRefProps = null;

				if (Platform.OS !== "web") {
					inputRefProps = {
						lastField: !(array && array[index + 1]),
						inputRef: (ele) => {
							inputRefs[index] = ele;
						},
						jumpToNext: () => {
							if (inputRefs && inputRefs[index + 1] && inputRefs[index + 1].focus) {
								inputRefs[index + 1].focus();
							}
						}
					};
				}

				return (
					<FormInput
						id={id_prefix + propertyName}
						key={propertyName}
						schema={propertySchema}
						defaultValue={defaultValue}
						onChange={doUpdate}
						name={propertyName}
						onSubmit={submit}
						contrast={contrast}
						errors={propertyErrors}
						labelStyle={labelStyle}
						externalCheck={externalForms}
						dataId={dataId}
						isATouchAway={isATouchAway}
						textStyle={textStyle}
						showCustomAddButton={showCustomAddButton}
						platform={platform}
						unsupportedFeatures={unsupportedFeatures}
						{...inputRefProps}
					/>
				);
			});

		const submitButton = this.renderSubmitAction();

		return (
			<View key={this.state.submitId} style={props.style}>
				{children}
				{props.children}
				{submitButton}
			</View>
		);
	}

	handleUpdate(name, value) {
		const hasChanged = this.state.hasChanged;
		const dataId = this.props.dataId;

		let existing;
		if (hasChanged) existing = this.state.data;
		else {
			const schemaDefaults = defaults(this.props.schema);
			existing = xtend(schemaDefaults, this.props.defaultData || {});
		}

		const update = {};
		update[name] = value;
		const newData = xtend(existing, update);

		//Wrap the next in chain as a callback to avoid having setState falling back to olf values when rechecking.
		this.setState({
			hasChanged: true,
			data: newData
		}, ()=>{
			this.context.timeoutReset && this.context.timeoutReset();

			if (!this.props.autoSubmit && this.props.submitType !== "auto") {
				if (dataId && this.saveEncryptStorage) {
					this.saveEncryptStorage(newData);
				}
				return;
			}
			if (!this.props.onSubmit) return;

			const newState = xtend(newData);

			this.props.onSubmit(newState);
		});
	}

	sanitizeData(data, schema) {
		const properties = schema.properties || {};
		const externalForms = this.props.externalForms;

		const sanitizeItem = (item, property) => {
			if (item === undefined || item === null) {
				return item;
			}

			if (property && property.type === "number") {
				return parseFloat(item);
			}

			if (property && property.type === "string") {
				return item.toString().trim();
			}

			if (typeof item === "string") {
				return item.trim();
			}

			return item;
		};

		const loop = (dataObject, objProperties) => {
			Object.keys(dataObject).forEach(function (k) {
				// Remove empty objects
				if (dataObject[k] && Array.isArray(dataObject[k]) && dataObject[k].length) {
					dataObject[k] = dataObject[k].filter((item) => {
						if (item !== undefined && item !== null && typeof item === "object" && Object.keys(item).length === 0) {
							return false;
						}

						return true;
					});

					if (dataObject[k].length === 0) {
						delete dataObject[k];
						return;
					}
				}

				if (dataObject[k] && typeof dataObject[k] === "object") {
					let inProperties;
					if (objProperties[k] && objProperties[k].inputType === "externalform" && externalForms) {
						const externalFormName = objProperties[k].external.split(":")[1];
						inProperties = externalForms[externalFormName]?.properties || {};
					} else {
						inProperties = objProperties[k]?.properties || {};
					}

					loop(dataObject[k], inProperties);
					return;
				}

				// remove empty numbers
				if (objProperties[k]?.type === "number" && dataObject[k] === "") {
					delete dataObject[k];
					return;
				}
				dataObject[k] = sanitizeItem(dataObject[k], objProperties[k]);
			});
		};

		loop(data, properties);
		return data;
	}

	validateSubmitData(data, next) {
		const schema = this.props.schema;
		const onSubmit = this.props.onSubmit;

		const externalErrs = this.props.errors;
		const sanitizedData = this.sanitizeData(data, schema);

		if (externalErrs) {
			onSubmit(sanitizedData);
		} else {
			const validate = makeValidator(schema, VALIDATOR_OPTIONS);

			validate(sanitizedData);
			let errors = validate.errors;

			if (errors) {
				errors = errors.filter(isValidError);
				console.log(errors);
			}

			if (errors && errors.length) {
				if (findError(errors, "has less length than allowed")) {
					const dispatch = this.context.dispatch || this.props.dispatch || noop;
					dispatch("toast:trigger:show", {
						content: "formerrors.missingrequired",
						style: "error"
					})();
				}
				this.setState({
					errors: formatErrors(errors)
				});
			} else {
				this.setState({
					errors: {}
				});
				next(sanitizedData);
			}
		}
	}

	doSubmit(data) {
		const onSubmit = this.props.onSubmit;

		if (!onSubmit) return;

		this.validateSubmitData(data, onSubmit);
	}
}

function formatErrors(errors) {
	const formatted = errors.map(processRawError);
	return errorUtils.formatErrors(formatted);
}

function processRawError(error) {
	const keys = error.field.split(".").slice(1);

	return {
		field: keys,
		message: error.message
	};
}

function isValidError(error) {
	const isEmpty = error.value === undefined || error.value === "";
	if (isEmpty && error.message !== "is required" && error.message !== "has less length than allowed") return false;
	return true;
}

Form.propTypes = {
	schema: PropTypes.object,
	defaultData: PropTypes.object,
	autoSubmit: PropTypes.bool,
	onSubmit: PropTypes.func,
	onCancel: PropTypes.func,
	submitText: PropTypes.string,
	cancelText: PropTypes.string
};

Form.contextTypes = {
	sources: PropTypes.object,
	dispatch: PropTypes.func,
	timeoutReset: PropTypes.func,
	localize: PropTypes.func,
	localizeFrom: PropTypes.func
};

Form.childContextTypes = {
	bigger: PropTypes.bool,
	sources: PropTypes.object,
	externalForms: PropTypes.object,
	localizeFrom: PropTypes.func,
	localize: PropTypes.func,
	dispatch: PropTypes.func
};

export default Form;

function defaults(schema) {
	return Object.keys(schema.properties || {}).reduce(function (values, property) {
		const defaultValue = schema.properties[property].default;
		if (defaultValue !== undefined) values[property] = defaultValue;
		return values;
	}, {});
}

function getSubmitData() {
	const hasChanged = this.state.hasChanged;
	const schema = this.props.schema;
	const schemaDefaults = defaults(schema);
	const defaultData = xtend(schemaDefaults, this.defaultValues || {});
	const result = (hasChanged ? this.state.data : defaultData) || {};

	return result;
}
function handleSubmit() {
	const data = this.getSubmitData();

	this.doSubmit(data);
}

function makeUpdater(name) {
	const component = this;
	return function onUpdate(value) {
		component.handleUpdate(name, value);
	};
}

function findError(errors, errorString) {
	return errors.reduce(function (found, error) {
		if (error.message === errorString) return found++;
		return found;
	}, 0);
}
